<template>
  <div class="qamp-wrapper">
    <dropdown
      :placement="placement"
      :class="{'qamp__stumpy': stumpy}"
      class="qamp"
      title="Add new members"
      @open="getCandidates"
      @close="resetCandidates"
    >
      <quick-add-button title="Add new members" />
      <template #content>
        <div>
          <sleek-filter-input
            v-model="searchQuery"
            :placeholder="placeholderText"
            show-small-text
          />
          <loading-placeholder v-if="isLoading" :lines="3" />
          <template v-else>
            <ul>
              <li
                v-for="person in candidatesToDisplay"
                :key="person.id"
                @click="toggleCandidate(person.id)"
              >
                <avatar :person="person" :size="20" />
                <span :class="{'selected' : isAdded(person.id)}">
                  {{person.name}}
                  <i v-if="person.isGuest" class="ddi ddi-warning" title="Guest user" />
                </span>
              </li>
            </ul>
            <div v-if="!hasCandidatesToDisplay" class="no-results">
              <span v-if="searchQueryIsEmail">
                Invite
                <div class="email-candidate">{{searchQuery}}</div>
                to this {{entityTypeName}}?
                <dd-button
                  :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
                  :is-loading="isSubmitting"
                  :color="buttonColor"
                  size="tiny"
                  @click="inviteMember(false)"
                >Invite new person</dd-button>
                <dd-button
                  v-if="canInviteAsGuest"
                  :is-loading="isSubmitting"
                  type="text"
                  size="tiny"
                  class="invite-as-guest"
                  @click="inviteMember(true)"
                >or invite as guest</dd-button>
              </span>
              <span v-else>
                {{noResultsText}}
              </span>
            </div>
          </template>
        </div>
      </template>
    </dropdown>
    <!-- Rendered as a sibling of the dropdown (not inside it) so el-dialog's overlay
         stacks against the page, not the dropdown's positioning context. -->
    <seat-limit-modal
      v-model:visible="isSeatLimitModalVisible"
      :details="seatLimitDetails"
      @upgraded="onSeatLimitUpgraded"
    />
    <guest-upgrade-prompt
      v-model:visible="isGuestUpgradeModalVisible"
      mode="modal"
      @upgraded="onGuestUpgraded"
    />
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { EEntities } from '@/types'
import { IAccountMembershipsInputInviteToProject } from '@/api/types/request.types'
import {
  IPersonWithEmail,
  IPlanLimitReachedDetails,
} from '@/api/types/response.types'

/* Components */
import GuestUpgradePrompt from '@/components/global/guest-upgrade-prompt.vue'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default defineComponent({
  name: 'QuickAddMemberPicker',

  components: {
    GuestUpgradePrompt,
  },

  props: {
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    stumpy: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['members-added'],

  data() {
    return {
      AppSumoAssistant,
      isLoading: true,
      isSubmitting: false,
      candidates: [] as IPersonWithEmail[],
      selectedCandidateIDs: [] as number[],
      searchQuery: null as any,
      debounceAddMembers: null as any,

      seatLimitDetails: null as IPlanLimitReachedDetails | null,
      isSeatLimitModalVisible: false,
      isGuestUpgradeModalVisible: false,
      // The dropdown's clickoutside fires when the user clicks an upgrade modal, which
      // triggers `resetCandidates` and nulls `searchQuery`. Stash the email at modal-open time
      // so the post-upgrade retry has it. Shared by seat-limit and guest-upgrade flows.
      pendingInviteEmail: null as string | null,
    }
  },

  computed: {
    buttonColor(): string {
      return ColorAssistant.getEntityColor(this.entityType)
    },
    placeholderText(): string {
      if (this.isLoading) {
        return ''
      }
      if (this.hasCandidatesToDisplay) {
        return 'Add by name or invite user by email...'
      }
      return 'Invite user by email...'
    },
    candidatesToDisplay(): IPersonWithEmail[] {
      if (!this.searchQuery) {
        return this.candidates
      }
      return this.candidates.filter(
        p =>
          p.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          p.email.toLowerCase().includes(this.searchQuery.toLowerCase()),
      )
    },
    hasCandidatesToDisplay(): boolean {
      return this.candidatesToDisplay.length > 0
    },
    hasSelectedCandidates(): boolean {
      return this.selectedCandidateIDs.length > 0
    },
    candidateSubmitText(): string {
      return this.selectedCandidateIDs.length <= 1
        ? 'Add member'
        : 'Add ' + this.selectedCandidateIDs.length + ' members'
    },
    searchQueryIsEmail(): boolean {
      if (!this.searchQuery) {
        return false
      }
      return TextAssistant.isValidEmail(this.searchQuery)
    },
    canInviteAsGuest(): boolean {
      return (
        this.entityType === EEntities.PROJECTS &&
        this.$permissions.isTieredPricingPlan
      )
    },
    entityTypeName(): string {
      return TextAssistant.entitySingularName(this.entityType).toLowerCase()
    },
    noResultsText(): string {
      if (this.searchQuery) {
        return 'No results found. Enter an email to invite someone new.'
      }
      /* Case where there are simply no candidates */
      return 'Enter an email to invite someone new.'
    },
  },

  created() {
    this.debounceAddMembers = debounce(this.addMembers, 500)
  },

  methods: {
    async toggleCandidate(id: number) {
      if (this.isAdded(id)) {
        this.selectedCandidateIDs = this.selectedCandidateIDs.filter(
          p => p !== id,
        )
      } else {
        this.selectedCandidateIDs.push(id)
      }

      await this.debounceAddMembers()
    },
    isAdded(id: number): boolean {
      return this.selectedCandidateIDs.includes(id)
    },
    resetCandidates() {
      this.searchQuery = null
      this.selectedCandidateIDs = []
    },
    async getCandidates() {
      this.isLoading = true
      if (this.entityType === EEntities.MAILBOXES) {
        this.candidates = await MailboxesAPI.get_MembershipCandidates(
          this.entityId,
        )
      } else {
        this.candidates = await InternalProjectsAPI.get_MembershipCandidates(
          this.entityId,
        )
      }
      this.isLoading = false
    },
    async addMembers() {
      try {
        if (!this.hasSelectedCandidates) {
          return
        }
        this.isSubmitting = true
        let response
        if (this.entityType === EEntities.MAILBOXES) {
          response = await MailboxesAPI.put_AddPeople(
            this.entityId,
            this.selectedCandidateIDs,
          )
        } else {
          response = await InternalProjectsAPI.put_AddPeople(
            this.entityId,
            this.selectedCandidateIDs,
          )
        }
        this.$messaging.showSuccess(response.message)

        /* Remove from assignable people */
        this.candidates = this.candidates.filter(
          p => !this.selectedCandidateIDs.includes(p.id),
        )
        this.$emit('members-added')
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.resetCandidates()
        this.isSubmitting = false
      }
    },
    async inviteMember(asGuest: boolean = false) {
      // Guest invite on a tiered account without the Guests capability: surface the
      // upgrade-to-Pro modal instead of calling the API. The dropdown will close (clickoutside
      // on modal open), so stash the email for the post-upgrade retry.
      if (asGuest && !this.$permissions.canHaveGuests) {
        this.pendingInviteEmail = this.searchQuery
        this.isGuestUpgradeModalVisible = true
        return
      }

      try {
        if (
          !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
            this.$router,
            this.$permissions,
          ))
        ) {
          return
        }

        this.isSubmitting = true
        const data = {
          email: this.searchQuery,
          projectID: this.entityId,
          asGuest,
        } as IAccountMembershipsInputInviteToProject

        const response = await AccountMembershipsAPI.post_InviteToProject(data)
        this.$messaging.showSuccess(response.message)
        this.$emit('members-added')
        this.resetCandidates()
      } catch (error) {
        if (this.tryHandleSeatLimit(error)) {
          this.isSubmitting = false
          return
        }
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
    tryHandleSeatLimit(error: any): boolean {
      const details: IPlanLimitReachedDetails | undefined =
        error && error.data && error.data.details
      if (!details || details.limitType !== 'seats') {
        return false
      }
      this.pendingInviteEmail = this.searchQuery
      this.seatLimitDetails = details
      this.isSeatLimitModalVisible = true
      return true
    },
    onSeatLimitUpgraded() {
      // Restore the email the user was trying to invite (the dropdown closed when the
      // modal was clicked, which reset searchQuery).
      if (this.pendingInviteEmail) {
        this.searchQuery = this.pendingInviteEmail
        this.pendingInviteEmail = null
      }
      this.inviteMember()
    },
    onGuestUpgraded() {
      // Post-upgrade, $permissions.canHaveGuests is now true. Restore the email and retry
      // the original guest-invite click.
      if (this.pendingInviteEmail) {
        this.searchQuery = this.pendingInviteEmail
        this.pendingInviteEmail = null
      }
      this.inviteMember(true)
    },
  },
})
</script>

<style lang="scss">
.qamp {
  .dropdown__popper {
    width: 258px;
  }

  ul {
    max-height: 268px;
    overflow: auto;
  }

  /* For dashboard card entities, make the dropdown shorter */
  &__stumpy {
    ul {
      max-height: 168px;
    }
  }

  .no-results {
    text-align: center;
    @include lettering--p-sm;
    padding: 10px 0;

    .email-candidate {
      @include single-line-ellipsis;
      font-weight: $bold;
      display: block;
    }

    .dd-button {
      margin: 0 auto;
      margin-top: 10px;
    }

    // Link-styled secondary action under the primary "Invite new person" button.
    // dd-button type="text" by itself reads as near-plain text; explicit color +
    // underline signals "this is clickable" without escalating to button weight.
    .invite-as-guest {
      color: $color--blue-80;
      text-decoration: underline;
      margin-top: 4px;

      &:hover {
        opacity: 0.75;
      }
    }
  }

  .ddi-warning {
    color: $color--orange-40 !important;
  }
}
</style>
